import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { Card, CardContent, Checkbox, SearchInput, Skeleton } from "@coval/ui"
import { Plus } from "lucide-react"

import { cn } from "@/utils/cn"

export interface SearchableItem {
  id: string
  label: string
  searchValue?: string
}

interface SearchableCheckboxListProps {
  items: SearchableItem[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  isLoading: boolean
  isDeferred?: boolean
  isOpen: boolean
  placeholder: string
  emptyMessage: string
  noItemsMessage?: string
  width?: string
  capitalizeLabels?: boolean
  renderItem?: (item: SearchableItem) => ReactNode
  allowExactValue?: boolean
  exactValueLabel?: string
  canAddItem?: (id: string) => boolean
  blockedAdditionMessage?: string
}

export const SearchableCheckboxList: React.FC<SearchableCheckboxListProps> = ({
  items,
  selectedIds,
  onSelectionChange,
  isLoading,
  isDeferred = false,
  isOpen,
  placeholder,
  emptyMessage,
  noItemsMessage,
  width = "w-80",
  capitalizeLabels = true,
  renderItem,
  allowExactValue = false,
  exactValueLabel = "Use exact value",
  canAddItem,
  blockedAdditionMessage,
}) => {
  const instanceId = useId()
  const [searchQuery, setSearchQuery] = useState("")
  const [additionRejected, setAdditionRejected] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timeoutId = window.setTimeout(
        () => searchInputRef.current?.focus(),
        100,
      )
      return () => window.clearTimeout(timeoutId)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("")
      setAdditionRejected(false)
    }
  }, [isOpen])

  const isAdditionAllowed = useCallback(
    (id: string) => canAddItem?.(id) !== false,
    [canAddItem],
  )

  const handleToggle = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        if (selectedIds.includes(id)) {
          return
        }
        if (!isAdditionAllowed(id)) {
          setAdditionRejected(true)
          return
        }
        onSelectionChange([...selectedIds, id])
        setAdditionRejected(false)
        return
      }

      onSelectionChange(selectedIds.filter((i) => i !== id))
      setAdditionRejected(false)
    },
    [isAdditionAllowed, selectedIds, onSelectionChange],
  )

  const exactValue = searchQuery.trim()
  const exactValueIsCandidate =
    allowExactValue &&
    exactValue.length > 0 &&
    !selectedIds.includes(exactValue) &&
    !items.some((item) => item.id === exactValue)
  const exactValueBlocked =
    exactValueIsCandidate && !isAdditionAllowed(exactValue)
  const canAddExactValue = exactValueIsCandidate && !exactValueBlocked
  const handleAddExactValue = useCallback(() => {
    if (!exactValueIsCandidate) {
      return
    }
    if (!isAdditionAllowed(exactValue)) {
      setAdditionRejected(true)
      return
    }
    onSelectionChange([...selectedIds, exactValue])
    setSearchQuery("")
    setAdditionRejected(false)
  }, [
    exactValue,
    exactValueIsCandidate,
    isAdditionAllowed,
    onSelectionChange,
    selectedIds,
  ])

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items
    }
    const query = searchQuery.toLowerCase()
    return items.filter((item) => {
      const searchTarget = item.searchValue ?? item.label
      return searchTarget.toLowerCase().includes(query)
    })
  }, [items, searchQuery])

  const hasBlockedVisibleItem = filteredItems.some(
    (item) => !selectedIds.includes(item.id) && !isAdditionAllowed(item.id),
  )
  const showBlockedAdditionMessage =
    blockedAdditionMessage &&
    (additionRejected || exactValueBlocked || hasBlockedVisibleItem)

  return (
    <Card className={`border-0 shadow-none w-full max-w-full ${width}`}>
      <CardContent className="p-0">
        <div className="p-3 border-b border-coval-border">
          <SearchInput
            ref={searchInputRef}
            aria-label={placeholder}
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setAdditionRejected(false)
            }}
            onClear={() => {
              setSearchQuery("")
              setAdditionRejected(false)
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && exactValueIsCandidate) {
                event.preventDefault()
                handleAddExactValue()
              }
            }}
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {isDeferred || isLoading ? (
            <div className="flex justify-center py-4">
              <Skeleton className="h-4 w-16 rounded-md" />
            </div>
          ) : (
            <div className="p-1.5">
              <div className="flex flex-col">
                {filteredItems.map((item) => {
                  const isDisabled =
                    !selectedIds.includes(item.id) &&
                    !isAdditionAllowed(item.id)
                  return (
                    <label
                      key={item.id}
                      className={cn(
                        "flex max-w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-coval-text",
                        isDisabled
                          ? "cursor-default"
                          : "cursor-pointer hover:bg-surface-contrast",
                      )}
                    >
                      <Checkbox
                        id={`${instanceId}-checkbox-${item.id}`}
                        checked={selectedIds.includes(item.id)}
                        disabled={isDisabled}
                        onCheckedChange={(checked) =>
                          handleToggle(item.id, !!checked)
                        }
                      />
                      <span className="min-w-0">
                        {renderItem ? (
                          renderItem(item)
                        ) : (
                          <span
                            className={`block whitespace-normal break-words ${capitalizeLabels ? "capitalize" : ""}`}
                          >
                            {item.label}
                          </span>
                        )}
                      </span>
                    </label>
                  )
                })}
                {exactValueIsCandidate && (
                  <button
                    type="button"
                    aria-label={`${exactValueLabel} ${exactValue}`}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-coval-text hover:bg-surface-contrast disabled:opacity-50"
                    disabled={!canAddExactValue}
                    onClick={handleAddExactValue}
                  >
                    <Plus className="size-3 shrink-0 text-coval-text-muted" />
                    <span className="shrink-0 text-coval-text-muted">
                      {exactValueLabel}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {exactValue}
                    </span>
                  </button>
                )}
                {showBlockedAdditionMessage && (
                  <div
                    role="status"
                    className="py-2 text-center text-xs text-coval-text-muted"
                  >
                    {blockedAdditionMessage}
                  </div>
                )}
                {filteredItems.length === 0 &&
                  searchQuery.trim() &&
                  !exactValueIsCandidate && (
                    <div className="py-2 text-center text-sm text-coval-text-muted">
                      {emptyMessage}
                    </div>
                  )}
                {filteredItems.length === 0 &&
                  !searchQuery.trim() &&
                  noItemsMessage && (
                    <div className="py-2 text-center text-sm text-coval-text-muted">
                      {noItemsMessage}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
